import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import {
  AppPageShell,
  AppLoadingState,
  AppPhoneField,
  Button,
  EmptyState,
  PageHeader,
  Panel,
} from './common/ui';

const ContactForm = ({ contact, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone_number: '',
    email: '',
    company: '',
    notes: '',
    ...contact
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700">Name *</label>
        <input
          type="text"
          value={formData.name}
          onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
          className="mt-1 block w-full app-field"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">Phone Number *</label>
        <AppPhoneField
          country="us"
          value={formData.phone_number}
          onChange={phone => setFormData(prev => ({ ...prev, phone_number: phone }))}
          inputClass="mt-1 block"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Email</label>
        <input
          type="email"
          value={formData.email || ''}
          onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
          className="mt-1 block w-full app-field"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Company</label>
        <input
          type="text"
          value={formData.company || ''}
          onChange={e => setFormData(prev => ({ ...prev, company: e.target.value }))}
          className="mt-1 block w-full app-field"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Notes</label>
        <textarea
          value={formData.notes || ''}
          onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
          rows="3"
          className="mt-1 block w-full app-field"
        />
      </div>

      <div className="flex justify-end space-x-3">
        <Button
          type="button"
          onClick={onCancel}
          variant="secondary"
        >
          Cancel
        </Button>
        <Button
          type="submit"
        >
          {contact ? 'Update Contact' : 'Add Contact'}
        </Button>
      </div>
    </form>
  );
};

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setIsLoading(true);
      const response = await api.request('/contacts');
      setContacts(Array.isArray(response) ? response : []);
      setError(null);
    } catch (err) {
      setError('Failed to fetch contacts');
      console.error('Error fetching contacts:', err);
      setContacts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      fetchContacts();
      return;
    }

    try {
      setIsLoading(true);
      const response = await api.request(`/contacts/search?query=${encodeURIComponent(query)}`);
      setContacts(Array.isArray(response) ? response : []);
      setError(null);
    } catch (err) {
      setError('Failed to search contacts');
      console.error('Error searching contacts:', err);
      setContacts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (formData) => {
    try {
      if (editingContact) {
        await api.request(`/contacts/${editingContact.id}`, {
          method: 'PUT',
          body: JSON.stringify(formData)
        });
      } else {
        await api.request('/contacts', {
          method: 'POST',
          body: JSON.stringify(formData)
        });
      }
      
      fetchContacts();
      setShowForm(false);
      setEditingContact(null);
    } catch (err) {
      setError(editingContact ? 'Failed to update contact' : 'Failed to create contact');
      console.error('Error saving contact:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) {
      return;
    }

    try {
      await api.request(`/contacts/${id}`, {
        method: 'DELETE'
      });
      fetchContacts();
    } catch (err) {
      setError('Failed to delete contact');
      console.error('Error deleting contact:', err);
    }
  };

  const handleSendCall = (contactId) => {
    navigate(`/app/send-call?contactId=${contactId}`);
  };

  return (
    <AppPageShell maxWidthClass="max-w-5xl">
      <PageHeader
        eyebrow="Resources"
        title="Contacts"
        description="Maintain reusable contact records for calls and campaigns."
        actions={(
          <Button
            onClick={() => {
              setShowForm(true);
              setEditingContact(null);
            }}
            className="w-full sm:w-auto"
          >
            Add New Contact
          </Button>
        )}
      />

      <div className="mb-6">
        <input
          type="text"
          placeholder="Search contacts..."
          value={searchQuery}
          onChange={e => handleSearch(e.target.value)}
          className="block w-full app-field"
        />
      </div>

      {error && (
        <div className="app-alert mb-4">
          {error}
        </div>
      )}

      {showForm && (
        <Panel className="mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            {editingContact ? 'Edit Contact' : 'Add New Contact'}
          </h2>
          <ContactForm
            contact={editingContact}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false);
              setEditingContact(null);
            }}
          />
        </Panel>
      )}

      {isLoading ? (
        <AppLoadingState layout="compact" showLabel={false} />
      ) : contacts.length === 0 ? (
        <EmptyState
          title={searchQuery ? 'No contacts found' : 'No contacts yet'}
          description={searchQuery ? 'Adjust the search query to find another contact.' : 'Add contacts once, then reuse them when placing calls.'}
        />
      ) : (
        <div className="app-table">
          <ul className="divide-y divide-gray-200">
            {Array.isArray(contacts) && contacts.map(contact => (
              <li key={contact.id} className="px-4 py-4 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-gray-900">{contact.name}</h3>
                    <p className="mt-1 text-sm text-gray-500">{contact.phone_number}</p>
                    {contact.email && (
                      <p className="mt-1 text-sm text-gray-500">{contact.email}</p>
                    )}
                    {contact.company && (
                      <p className="mt-1 text-sm text-gray-500">{contact.company}</p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 justify-end">
                    <Button
                      onClick={() => handleSendCall(contact.id)}
                      variant="subtle"
                      size="sm"
                    >
                      Send Call
                    </Button>
                    <Button
                      onClick={() => {
                        setEditingContact(contact);
                        setShowForm(true);
                      }}
                      variant="ghost"
                      size="sm"
                    >
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDelete(contact.id)}
                      variant="danger"
                      size="sm"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </AppPageShell>
  );
};

export default Contacts; 
